import { createSelector } from '@reduxjs/toolkit';
import type { Filters } from '@/features/filters';
import { prepareFilters } from '@/features/filters';
import type { OrganizationRole, ViewsRestrictions } from '@/entities/session';
import { selectUserId, updateViewsRestrictions } from '@/entities/session';
import { DataStatus, isError, isLoading } from '@/shared/lib';
import { createAppSlice } from '@/shared/model';
import * as UserManagementAPI from '../api/userManagementAPI';
import type { UserSettingsMeta, User, CLSFilters } from './types';

interface State {
  users:
    | {
        data: User[];
        status: DataStatus.finished;
      }
    | {
        data: null;
        status: Exclude<DataStatus, DataStatus.finished>;
      };
  userSettingsMeta:
    | {
        data: UserSettingsMeta;
        status: DataStatus.finished;
      }
    | {
        data: null;
        status: Exclude<DataStatus, DataStatus.finished>;
      };
}

const initialState: State = {
  users: {
    data: null,
    status: DataStatus.idle,
  },
  userSettingsMeta: {
    data: null,
    status: DataStatus.idle,
  },
};

export const userManagementSlice = createAppSlice({
  name: 'userManagement',
  initialState,
  reducers: (create) => ({
    setupUsers: create.asyncThunk(
      (_: void) => {
        return UserManagementAPI.getOrganizationUsers();
      },
      {
        pending: (state) => {
          state.users.status = DataStatus.loading;
        },
        fulfilled: (state, action) => {
          state.users.data = action.payload;
          state.users.status = DataStatus.finished;
        },
        rejected: (state) => {
          state.users.status = DataStatus.error;
        },
      },
    ),
    inviteUser: create.asyncThunk<
      { email: string; role: OrganizationRole },
      User
    >(
      ({ email, role }) => {
        return UserManagementAPI.inviteUserToOrganization(email, role);
      },
      {
        pending: (state) => {
          state.users.status = DataStatus.loading;
        },
        fulfilled: (state, action) => {
          if (!state.users.data) {
            return;
          }

          state.users.data.push(action.payload);
          state.users.status = DataStatus.finished;
        },
        rejected: (state) => {
          state.users.status = DataStatus.finished;
        },
      },
    ),
    updateUser: create.asyncThunk<
      {
        user: User;
        update: {
          role?: OrganizationRole;
          isActive?: boolean;
          rlsFilters?: Filters;
          clsFilters?: CLSFilters;
          viewsRestrictions?: ViewsRestrictions;
        };
        silent?: boolean;
      },
      User
    >(
      ({ user, update }, { getState, dispatch }) => {
        const state = getState() as RootState;
        const currentUserId = selectUserId(state);
        const useSettingsMeta = selectUserSettingsMeta(state);
        const newFilters = update.rlsFilters ?? user.rlsFilters;
        const clusterisedFilters = prepareFilters(
          newFilters,
          [],
          useSettingsMeta?.rlsColumns || [],
        );
        const updatedUser = {
          id: user.id,
          ...(update.role ? { role: update.role } : {}),
          ...(update.isActive != null ? { isActive: update.isActive } : {}),
          ...(update.rlsFilters
            ? {
                rlsFilters: {
                  dimensionFilters: clusterisedFilters.dimensionsFilters,
                },
              }
            : {}),
          ...(update.clsFilters ? { clsFilters: update.clsFilters } : {}),
          ...(update.viewsRestrictions
            ? { viewsRestrictions: update.viewsRestrictions }
            : {}),
        };

        return UserManagementAPI.updateUser(updatedUser).then((user) => {
          if (user.id === currentUserId && update.viewsRestrictions) {
            dispatch(updateViewsRestrictions(user.viewsRestrictions));
          }

          return user;
        });
      },
      {
        pending: (state, action) => {
          if (action.meta.arg.silent) {
            return;
          }

          state.users.status = DataStatus.loading;
        },
        fulfilled: (state, action) => {
          if (!state.users.data) {
            return;
          }

          state.users.data = state.users.data.map((user) => {
            if (user.id === action.payload.id) {
              return {
                ...user,
                ...action.payload,
              };
            }
            return user;
          });

          if (!action.meta.arg.silent) {
            state.users.status = DataStatus.finished;
          }
        },
        rejected: (state, action) => {
          if (action.meta.arg.silent) {
            return;
          }

          state.users.status = DataStatus.finished;
        },
      },
    ),
    resendInvite: create.asyncThunk<string, void>(
      (email) => {
        return UserManagementAPI.resendInvite(email);
      },
      {
        pending: (state) => {
          state.users.status = DataStatus.loading;
        },
        fulfilled: (state) => {
          state.users.status = DataStatus.finished;
        },
        rejected: (state) => {
          state.users.status = DataStatus.finished;
        },
      },
    ),
    setupUserSettingsMeta: create.asyncThunk(
      (_: void) => {
        return UserManagementAPI.getUserSettingsMeta();
      },
      {
        pending: (state) => {
          state.userSettingsMeta.status = DataStatus.loading;
        },
        fulfilled: (state, action) => {
          state.userSettingsMeta.data = action.payload;
          state.userSettingsMeta.status = DataStatus.finished;
        },
        rejected: (state) => {
          state.userSettingsMeta.status = DataStatus.error;
        },
        options: {
          condition: (_, { getState }) => {
            const state = getState() as RootState;

            if (isLoading(state.userManagement.userSettingsMeta.status)) {
              return false;
            }
          },
        },
      },
    ),
  }),
  selectors: {
    selectUsers: (state) => {
      return state.users.data;
    },
    selectIsUsersLoading: (state) => {
      return isLoading(state.users.status);
    },
    selectIsUsersFailed: (state) => {
      return isError(state.users.status);
    },
    selectUserSettingsMeta: (state) => {
      return state.userSettingsMeta.data;
    },
    selectIsUserSettingsMetaLoading: (state) => {
      return isLoading(state.userSettingsMeta.status);
    },
    selectIsUserSettingsMetaFailed: (state) => {
      return isError(state.userSettingsMeta.status);
    },
    selectUserById: createSelector(
      (state) => state.users.data,
      (_, userId: string) => {
        return userId;
      },
      (users: User[], userId: string) => {
        return users?.find((user: User) => user.id === userId);
      },
    ),
  },
});

export const {
  inviteUser,
  setupUsers,
  updateUser,
  setupUserSettingsMeta,
  resendInvite,
} = userManagementSlice.actions;
export const {
  selectUsers,
  selectIsUsersLoading,
  selectIsUsersFailed,
  selectUserSettingsMeta,
  selectIsUserSettingsMetaLoading,
  selectIsUserSettingsMetaFailed,
  selectUserById,
} = userManagementSlice.selectors;
